// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TourRoundSummary } from '@/features/round-init/types';

/**
 * Rounds can exist before a course is chosen; the placeholder the round-init flow stores
 * is an empty id or the literal "n/a", and both mean the same thing to a preview.
 */
export const normalizeCourseId = (courseId: string | null | undefined): string | null => {
  const trimmed = courseId?.trim() ?? '';
  return trimmed === '' || trimmed.toLowerCase() === 'n/a' ? null : trimmed;
};

export type PreviewCourse = {
  id: string;
  name: string;
  clubName: string | null;
  city: string | null;
  lat: number;
  lon: number;
  holes: { number: number; par: number; strokeIndex: number; lengthMeters: number }[];
};

export type PreviewRound = {
  id: string;
  name: string | null;
  status: string | null;
  playDate: string | null;
  holesCount: number | null;
  courseId: string | null;
  courseName: string | null;
};

export type TourPreviewState = {
  tourName: string | null;
  rounds: PreviewRound[];
  /** Detail per distinct course the tour plays, keyed by course id. */
  coursesById: Record<string, PreviewCourse>;
  /** Earliest and latest known play date, ISO. Null when no round has a date yet. */
  firstPlayDate: string | null;
  lastPlayDate: string | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
};

type CourseDetailResponse = {
  id?: string;
  name?: string;
  clubName?: string;
  city?: string;
  lat?: number;
  lon?: number;
  holes?: { number?: number; par?: number; strokeIndex?: number; lengthMeters?: number }[];
};

/**
 * Everything the tour preview screen shows: the tour's name, its rounds in play order,
 * and the details of each distinct course those rounds visit.
 *
 * The rounds list is the same query my-tours renders, so a round the user can see there
 * appears here too — including drafts whose course has not been chosen yet.
 */
export const useTourPreview = (
  apiBaseUrl: string,
  token: string | null | undefined,
  tourId: string | null
): TourPreviewState => {
  const [tourName, setTourName] = useState<string | null>(null);
  const [rounds, setRounds] = useState<PreviewRound[]>([]);
  const [coursesById, setCoursesById] = useState<Record<string, PreviewCourse>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!token || !tourId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(false);

    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      try {
        const [tourResponse, roundsResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/tours/${encodeURIComponent(tourId)}`, { headers }),
          fetch(
            `${apiBaseUrl}/api/rounds/tour/${encodeURIComponent(tourId)}?status=draft,open,active,playing,started,finished`,
            { headers }
          ),
        ]);
        if (!tourResponse.ok || !roundsResponse.ok) throw new Error('load failed');

        const tour = (await tourResponse.json()) as { name?: string };
        const summaries = (await roundsResponse.json()) as TourRoundSummary[];
        const mapped: PreviewRound[] = summaries.map((round) => ({
          id: round.id,
          name: round.name ?? null,
          status: round.status ?? null,
          playDate: round.playDate ?? null,
          holesCount: round.holesCount ?? null,
          courseId: normalizeCourseId(round.courseId),
          courseName: round.courseName?.trim() || null,
        }));

        const courseIds = [
          ...new Set(mapped.map((round) => round.courseId).filter((id): id is string => !!id)),
        ];
        const details = await Promise.all(
          courseIds.map(async (courseId) => {
            try {
              const response = await fetch(
                `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}`,
                { headers }
              );
              if (!response.ok) return null;
              const payload = (await response.json()) as CourseDetailResponse;
              const course: PreviewCourse = {
                id: courseId,
                name: payload.name ?? '',
                clubName: payload.clubName?.trim() || null,
                city: payload.city?.trim() || null,
                lat: payload.lat ?? 0,
                lon: payload.lon ?? 0,
                holes: (payload.holes ?? [])
                  .map((hole) => ({
                    number: hole.number ?? 0,
                    par: hole.par ?? 0,
                    strokeIndex: hole.strokeIndex ?? 0,
                    lengthMeters: hole.lengthMeters ?? 0,
                  }))
                  .sort((a, b) => a.number - b.number),
              };
              return course;
            } catch {
              // One unreachable course must not take the whole preview down with it.
              return null;
            }
          })
        );

        if (cancelled) return;
        setTourName(tour.name?.trim() || null);
        setRounds(mapped);
        setCoursesById(
          Object.fromEntries(
            details
              .filter((course): course is PreviewCourse => course !== null)
              .map((course) => [course.id, course])
          )
        );
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, tourId, reloadKey]);

  const { firstPlayDate, lastPlayDate } = useMemo(() => {
    const dates = rounds
      .map((round) => round.playDate)
      .filter((date): date is string => !!date)
      .sort();
    return {
      firstPlayDate: dates[0] ?? null,
      lastPlayDate: dates[dates.length - 1] ?? null,
    };
  }, [rounds]);

  return { tourName, rounds, coursesById, firstPlayDate, lastPlayDate, loading, error, retry };
};
